import type { PostHogConfig } from 'posthog-js';
import { CLIENT_BUILD } from './build-info.js';
import { errorCapture, type CaptureError, type ErrorContext } from '../shared/error-tracking.js';
import { cloudProperties, type Observe } from '../shared/observability.js';
import { browserInstallationId, pageSessionId } from './observation-context.js';
import { BrowserCloudBudget } from './cloud-budget.js';

declare const __POSTHOG_CONFIG__: { token: string; host: string; environment: string; verificationRun?: string };
let capture: CaptureError = () => undefined;
let context: ErrorContext = {};
let started: Promise<unknown> | undefined;
let sendObservation: Observe = () => undefined;
let pendingObservations = 0;
let onBudgetExhausted: (kind: 'activity' | 'exception') => void = () => undefined;
const budget = new BrowserCloudBudget((kind) => onBudgetExhausted(kind));
export const captureBrowserObservation: Observe = (event, fields) => sendObservation(event, fields);

export const browserErrorOptions = (host: string, publicToken?: string): Partial<PostHogConfig> => ({
  api_host: host,
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  capture_exceptions: false, // Existing handlers capture once, including caught React errors.
  disable_session_recording: true,
  disable_surveys: true,
  capture_performance: false,
  capture_heatmaps: false,
  capture_dead_clicks: false,
  advanced_disable_flags: true,
  person_profiles: 'never',
  persistence: 'memory',
  before_send: (event) => {
    try {
      if (!event) return null;
      const properties = cloudProperties(event.event, { ...event.properties, eventId: event.properties?.eventId ?? event.uuid ?? crypto.randomUUID() });
      if (!properties || !budget.admit(event.event === '$exception')) return null;
      return { ...event, properties: { ...properties, distinct_id: browserInstallationId, ...(publicToken ? { token: publicToken } : {}) } };
    } catch { return null; } // Fail closed even if an unexpected SDK property cannot be inspected.
  }
});

export function installBrowserErrorTracking(sessionId: string, onExhausted?: (kind: 'activity' | 'exception') => void, local?: CaptureError) {
  if (onExhausted) onBudgetExhausted = onExhausted;
  if (started) return started;
  capture = errorCapture(() => undefined, { service: 'web', buildId: CLIENT_BUILD.buildId, sessionId }, { maxPending: 0, local });
  if (typeof __POSTHOG_CONFIG__ === 'undefined' || !__POSTHOG_CONFIG__.token) return;
  const config = __POSTHOG_CONFIG__;
  // The no-external entry point bundles exception support and never downloads
  // replay/survey/analytics extensions. Loading it cannot block rendering.
  const client = import('posthog-js/no-external').then(({ default: posthog }) => {
    posthog.init(config.token, { ...browserErrorOptions(config.host, config.token), bootstrap: { distinctID: browserInstallationId } });
    return posthog;
  });
  void client.catch(() => undefined);
  capture = errorCapture(async (error, properties) => (await client).captureException(error, properties), {
    service: 'web', platform: 'web', buildId: CLIENT_BUILD.buildId, environment: config.environment, sessionId, installationId: browserInstallationId, verificationRun: config.verificationRun
  }, { maxPending: 50, local });
  sendObservation = (event, fields = {}) => {
    if (pendingObservations >= 100) return;
    pendingObservations++;
    const eventId = typeof fields.eventId === 'string' ? fields.eventId : crypto.randomUUID();
    const timestamp = new Date();
    // No unbounded promise backlog while the SDK loads or fails.
    void client.then((posthog) => posthog.capture(event, { ...fields, eventId, service: 'web', platform: 'web', buildId: CLIENT_BUILD.buildId,
      environment: config.environment, sessionId: pageSessionId, installationId: browserInstallationId, verificationRun: config.verificationRun }, { uuid: eventId, timestamp })).catch(() => undefined).finally(() => { pendingObservations--; });
  };
  started = client;
  return started;
}

export function updateBrowserErrorContext(fields: { tripId?: string; planId?: string }) {
  if (fields.tripId && fields.tripId !== context.tripId) context = { tripId: fields.tripId };
  if (fields.planId) context.planId = fields.planId;
}

export function reportBrowserError(error: unknown, fields: ErrorContext = {}) {
  capture(error, { ...context, ...fields });
}
